/**
 * PLAN DECISION PICKLIST -- the founder's 6a control. NOT WIRED, NOT PRODUCTION.
 *
 * FOUNDER VISUAL VERDICT, 2026-09-10: "Treatment decision = ONE labelled
 * Decision picklist. Options are the actions Accept / Decline / Defer; the
 * control displays the SAVED state -- Accepted / Declined / Deferred -- or an
 * explicit undecided state when none is saved. Never default an undecided
 * treatment to Accept, silently or otherwise. Existing save behaviour and
 * required reasons preserved."
 *
 * IT LIVES HERE, AND IT IS HANDED THROUGH THE SEAM. The control it replaces is
 * a bare `<select>` inside Lane 1's ConsultWorkspacePage.tsx (the plan-row
 * "Change" disclosure, :2574-2589, and the add-item form, :1410-1426). This
 * lane does not edit that file. So the picklist ships as a component Lane 1
 * mounts, with the exact call site written out in the run record's FOR LANE 1
 * section -- and the integrated demonstration shows it in place the same way
 * the history panel is shown: injected at review time, never wired.
 *
 * NO NEW PRIMITIVE. It composes FormField + AcSelect from
 * `@/dashboard/ui/FormControl`, which the design-system conformance guard names
 * as THE owner of the raw `<select>`; the decline reasons come from the ONE
 * existing vocabulary (`OUTCOME_REASON_OPTIONS`, owner/Lead360Panel.tsx:238),
 * and the saved-state words from the ONE label dictionary
 * (`planItemStatusLabel`, clinical-workbench/case-sheet-labels.ts:128). Nothing
 * is forked and no second vocabulary is introduced.
 *
 * THE DEFAULT-TO-ACCEPT TRAP, and why the undecided option is rendered at all.
 * A native `<select>` whose `value` matches no option selects the FIRST option
 * -- so a control offering only Accept/Decline/Defer would DISPLAY "Accept" for
 * a treatment nobody has decided. That is the exact failure the founder ruled
 * out. The undecided entry is therefore PRESENT while the saved status is
 * undecided, and `disabled` so it can be shown but never chosen: a doctor can
 * move OUT of undecided, never INTO it, and the control can never invent a
 * decision that was not made.
 *
 * A DELIBERATE COMMIT STEP, AND WHY IT IS NOT OPTIONAL.
 * The first cut saved on the select's `change` event. **Chrome fires `change`
 * on EVERY arrow key of a closed native select**, so a doctor arrowing from
 * "no decision" toward Decline WROTE "Accepted" in passing -- an incorrect
 * write of a decision nobody made, to an audited clinical record. Typeahead
 * avoids the path; it does not fix the defect, because the arrow keys are
 * ordinary use.
 *
 * REUSED, NOT INVENTED: the shipped plan-row editor already solves this. Its
 * `<select>` stages a local edit and a SEPARATE `AcButton` Save commits it
 * (`ConsultWorkspacePage.tsx:2629-2636`, `onSave`), so intermediate selections
 * write nothing. This control adopts that exact shape -- native select for
 * choosing, one Save beside it -- keeping the founder's "ONE labelled Decision
 * picklist" while making an intermediate arrow key harmless.
 *
 * SERVER-CONFIRMED MEANS SERVER-CONFIRMED. `status` and `declineReason` are the
 * LAST CONFIRMED values -- what a server response actually carried. The control
 * never displays a requested value as though it were saved: on a rejection, a
 * network failure, or a response that omits this row, it falls back to the last
 * confirmed value, LABELS it as such, and says what happened. A network failure
 * in particular does not prove the server left the record unchanged, and the
 * copy says so rather than implying it.
 *
 * WHAT IT DOES NOT TOUCH: the save. `onCommit` hands the caller the values to
 * write, and the caller's existing write is the only writer. This component
 * fires no fetch and decides nothing on its own; the only state it holds is the
 * staged, uncommitted selection.
 */
import { useEffect, useState } from 'react';
import { usePlanControlDraft, type PlanDraftOwner } from './consult-autosave';
import type { JSX } from 'react';

import AcButton from '@/dashboard/ui/AcButton';
import { PortalApiError } from '@/lib/portal-api';

import { AcSelect, FormField } from '@/dashboard/ui/FormControl';
import { planItemStatusLabel } from '@/dashboard/clinical-workbench/case-sheet-labels';
/* THE ONE DECLINE-REASON VOCABULARY, and its own label function. The SHIPPED
   consult imports this exact pair from this exact module
   (`ConsultWorkspacePage.tsx:38-41`), so this is not a new cross-module reach
   -- it is the same one, matched. `outcomeReasonLabel` is used rather than a
   local `.find()` over the options: the module already exports the lookup, and
   a second one drifts the moment a token is added. */
import { OUTCOME_REASON_OPTIONS, outcomeReasonLabel } from '@/dashboard/owner/Lead360Panel';
import { CASE_SHEET_LABELS } from '@/dashboard/clinical-workbench/case-sheet-labels';

/** The three ACTIONS a doctor takes. Deliberately not the same vocabulary as
 *  the saved states below -- an action is a verb, a state is what happened. */
export type PlanDecisionAction = 'advised' | 'accepted' | 'declined' | 'deferred';

export const PLAN_DECISION_ACTIONS: ReadonlyArray<{
  readonly status: PlanDecisionAction;
  readonly verb: string;
}> = [
  { status: 'accepted', verb: 'Accept' },
  { status: 'declined', verb: 'Decline' },
  { status: 'deferred', verb: 'Defer' },
];

/** The control's own label -- ONE word, because the row above it already names
 *  the treatment. */
export const PLAN_DECISION_LABEL = 'Decision';

/** The value carried by the undecided entry. Empty string, so it can never
 *  collide with a real status token. */
export const PLAN_DECISION_UNDECIDED_VALUE = '';

/**
 * The EXPLICIT undecided state. Not "Advised", not "Pending", not blank: those
 * are storage tokens and a blank reads as a control that failed to load. It
 * says, in words, that no decision has been recorded.
 */
export const PLAN_DECISION_UNDECIDED_LABEL = 'No decision recorded';

/**
 * Is this saved status a recorded DECISION? Pure; exported so the regression
 * drives the rule rather than restating it.
 *
 * ALLOWLISTED, NOT DENYLISTED. The first cut asked "is it one of advised /
 * pending / empty?" and answered NO for a token like `wat` -- so an unknown
 * value counted as a decision. It is not one: `advised` is a recommendation,
 * `pending` is the storage default, and a token this product does not
 * recognise is not evidence that anyone decided anything. Only the three
 * decisions below are decisions, and a denylist can never say that, because it
 * has to predict every value that is not one.
 */
export function isPlanDecisionRecorded(status: string | null | undefined): boolean {
  return PLAN_DECISION_ACTIONS.some((a) => a.status === status);
}

/**
 * What the control DISPLAYS for a saved status: the state word from the label
 * dictionary when a decision exists, and the explicit undecided line when none
 * does. An UNKNOWN token is treated as undecided rather than shown raw -- a
 * token the product does not recognise is not evidence that anyone decided.
 */
export function planDecisionDisplay(status: string | null | undefined): string {
  return isPlanDecisionRecorded(status)
    ? planItemStatusLabel(status as string)
    : PLAN_DECISION_UNDECIDED_LABEL;
}

/** The `<select>` value for a saved status: the status itself when it is a
 *  decision, else the undecided entry -- never the first action. */
export function planDecisionSelectValue(status: string | null | undefined): string {
  return isPlanDecisionRecorded(status)
    ? (status as string)
    : PLAN_DECISION_UNDECIDED_VALUE;
}

/**
 * WHY a save did not confirm. FOUR DIFFERENT FACTS, and the control must not
 * blur them:
 *   `rejected`     -- the server ANSWERED with a CLIENT error (4xx). It
 *                     decided, and nothing was saved.
 *   `server_error` -- the server answered with its OWN error (5xx). It was
 *                     reached, and whether the write applied is unknown.
 *   `unconfirmed`  -- the call returned 2xx, and the response did not carry
 *                     this row. The write may or may not have happened.
 *   `no_reply`     -- no usable reply at all (a network failure). This proves
 *                     NOTHING about what the server did.
 *
 * THE FOURTH CODE IS NOT DECORATION. The first cut had three, so a 5xx had to
 * be filed as one of the other two -- and both sentences are FALSE of it:
 * "rejected ... nothing was saved" claims a decision nobody made, and "did not
 * reach a reply" claims a silence that did not happen. A 5xx is its own fact
 * and gets its own sentence.
 *
 * THE CALLER CLASSIFIES; THIS FILE OWNS THE WORDS. The first version took a
 * free-text `why` from the caller and interpolated it into the sentence a
 * doctor reads -- which put raw `Error.message` text ("Failed to fetch") into
 * patient-workflow copy, and let a caller say anything at all. The caller now
 * passes one of four codes and the copy below is fixed.
 *
 * IT IS THE COPY FOR A PLAN-LINE WRITE, not for the decision alone: the same
 * line's doctor assignment and its remark go through the same endpoint and are
 * read by the same doctor, and a second vocabulary for them would be a second
 * vocabulary for one fact. Every sentence therefore says "this change" /
 * "this treatment", never "this decision".
 */
export type PlanDecisionFailure =
  | 'rejected'
  | 'server_error'
  | 'unconfirmed'
  | 'no_reply';

/** The ONE sentence per failure. Exported so the walk and the regression read
 *  exactly what the doctor reads. */
export const PLAN_DECISION_FAILURE_COPY: Record<PlanDecisionFailure, string> = {
  rejected: 'The clinic system rejected this change, so nothing was saved.',
  server_error:
    'The clinic system reported an error. Whether this change was saved is not confirmed.',
  unconfirmed:
    'The save returned without this treatment in it, so the change is not confirmed.',
  no_reply:
    'The save did not reach a reply. That does not prove the server left this unchanged.',
};

/**
 * CLASSIFY a caught write failure into one of the four facts above. It lives
 * HERE rather than in each caller, so every write on a plan line reports the
 * same fact for the same failure -- and so the rule is ONE testable function
 * rather than a regex over `Error.message` repeated per call site.
 *
 * IT READS THE STATUS, NOT THE MESSAGE. `PortalApiError` carries the real HTTP
 * status (portal-api.ts:31-38); a genuine transport failure is not a
 * `PortalApiError` at all, because `fetch` rejects before any envelope is
 * parsed. Substring-matching the message is the exact bug LES-009 records.
 *
 * `unconfirmed` is NOT returned here: it is not a thrown failure at all but a
 * successful response that omitted the row, which only the caller can see.
 */
export function classifyPlanWriteFailure(err: unknown): PlanDecisionFailure {
  if (err instanceof PortalApiError) {
    return err.status >= 400 && err.status < 500 ? 'rejected' : 'server_error';
  }
  return 'no_reply';
}

/** What the LAST SAVE ATTEMPT did. The caller owns this; the control only
 *  renders it, and refuses to show a requested value as a saved one. */
export type PlanDecisionOutcome =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'confirmed' }
  | { readonly kind: 'failed'; readonly failure: PlanDecisionFailure };

/** The copy for each unhappy outcome. Written once, so the walk and the
 *  regression read the same words the doctor does. */
export const PLAN_DECISION_UNCONFIRMED_PREFIX = 'Not confirmed';
export const PLAN_DECISION_LAST_CONFIRMED_PREFIX = 'Last confirmed';
export const PLAN_DECISION_SAVE_LABEL = 'Save decision';

export interface PlanDecisionPicklistProps {
  /** Later frozen V14g amendment; legacy picklist callers retain their shape. */
  readonly presentation?: 'select' | 'segments';
  readonly onPreview?: (status: string) => void;
  /** A stable id for the label/control pair (the caller owns row identity). */
  readonly id: string;
  /** The treatment this decision belongs to -- for the accessible name only. */
  readonly procedureName: string;
  /** The LAST CONFIRMED status -- what a server response actually carried.
   *  null / advised / pending / unknown all render the explicit undecided
   *  state. NEVER a requested-but-unconfirmed value. */
  readonly status: string | null;
  /** The LAST CONFIRMED decline reason, or '' when none. */
  readonly declineReason: string;
  /** Commit the staged decision. The caller owns the write. */
  readonly onCommit: (status: PlanDecisionAction, declineReason: string) => void | Promise<boolean>;
  readonly draftOwner?: PlanDraftOwner;
  /** What the last save attempt did. Defaults to idle. */
  readonly outcome?: PlanDecisionOutcome;
  readonly disabled?: boolean;
  /** A receipt owner keeps uncertain writes protected until verified readback. */
  readonly operationPending?: boolean;
  readonly retryPending?: boolean;
  readonly allowedActions?: readonly PlanDecisionAction[];
  readonly testId?: string;
}

export default function PlanDecisionPicklist({
  id,
  procedureName,
  status,
  declineReason,
  onCommit,
  outcome = { kind: 'idle' },
  disabled = false,
  testId, draftOwner, presentation = 'select', onPreview, operationPending = false, retryPending = false, allowedActions,
}: PlanDecisionPicklistProps): JSX.Element {
  const confirmedValue = presentation === 'segments' ? (status ?? PLAN_DECISION_UNDECIDED_VALUE) : planDecisionSelectValue(status);
  /** The STAGED selection -- local, uncommitted, and written nowhere. null
   *  means "nothing staged", so the control shows the confirmed value. */
  const [staged, setStaged] = useState<{ status: string; declineReason: string } | null>(null);

  /* ANY SETTLED OUTCOME CLEARS THE STAGING, so the control falls back to the
     LAST CONFIRMED value. That is the whole point of item 2: a rejected or
     unconfirmed save must not leave the requested value sitting on screen
     looking recorded. */
  useEffect(() => {
    if (outcome.kind === 'confirmed' || (outcome.kind === 'failed' && !draftOwner)) {
      setStaged(null);
    }
  }, [outcome.kind, outcome.kind === 'failed' ? outcome.failure : '', draftOwner]);

  const shownValue = staged?.status ?? confirmedValue;
  const shownReason = staged?.declineReason ?? declineReason;
  const undecided = shownValue === PLAN_DECISION_UNDECIDED_VALUE;
  const dirty =
    staged !== null &&
    (staged.status !== confirmedValue || staged.declineReason !== declineReason);
  const busy = disabled || outcome.kind === 'saving';
  const reasonId = `${id}-reason`;
  const settledBadly = outcome.kind === 'failed';
  useEffect(() => { onPreview?.(shownValue); }, [shownValue, onPreview]);
  usePlanControlDraft(draftOwner, `${id}:decision`, { dirty: dirty || operationPending,
    signature: JSON.stringify([staged,operationPending]), pending: outcome.kind === 'saving' || operationPending, retryPending,
    commit: () => staged ? onCommit(staged.status as PlanDecisionAction, staged.declineReason) : true,
    reset: () => setStaged(null),
  });

  return (
    <div className="ac-opsrow" data-testid={testId ?? 'plan-decision-picklist'}>
      {presentation === 'segments' ? <div className="consult-v14g__answer" role="group" aria-label={`Patient's answer for ${procedureName}`} data-testid={`${testId ?? 'plan-decision'}-select`} data-value={shownValue}>
        {(['advised','accepted','declined'] as const).map(value => <AcButton key={value} size="sm" variant="ghost" aria-pressed={shownValue === value} disabled={busy || !!allowedActions && !allowedActions.includes(value)}
          onClick={() => setStaged({status:value, declineReason:value === 'declined' ? shownReason : ''})}><span aria-hidden="true">{value==='accepted'?'✓':value==='declined'?'×':'○'}</span> {planItemStatusLabel(value)}</AcButton>)}
        {!['advised','accepted','declined'].includes(shownValue) ? <span className="consult-v14g__muted">{planDecisionDisplay(status)}</span> : null}
      </div> : <FormField id={id} label={PLAN_DECISION_LABEL}>
        <AcSelect
          id={id}
          aria-label={`${PLAN_DECISION_LABEL} for ${procedureName}`}
          value={shownValue}
          disabled={busy}
          data-testid={`${testId ?? 'plan-decision'}-select`}
          onChange={(e) => {
            const next = e.target.value;
            // The undecided entry is disabled and cannot be chosen; this guard
            // makes that true in code as well as in markup.
            if (next === PLAN_DECISION_UNDECIDED_VALUE) return;
            /* STAGE ONLY. NOTHING IS WRITTEN HERE -- this is the fix for the
               arrow-key defect: `change` fires on every arrow of a closed
               select, so anything that writes from this handler writes
               decisions nobody made. */
            setStaged({
              status: next,
              // Moving away from Declined drops the reason; moving back keeps
              // whatever was last confirmed rather than inventing one.
              declineReason: next === 'declined' ? shownReason : '',
            });
          }}
        >
          {undecided ? (
            <option value={PLAN_DECISION_UNDECIDED_VALUE} disabled>
              {PLAN_DECISION_UNDECIDED_LABEL}
            </option>
          ) : null}
          {PLAN_DECISION_ACTIONS.map((a) => (
            <option key={a.status} value={a.status} disabled={!!allowedActions && !allowedActions.includes(a.status)}>
              {/* The option is the ACTION; once CONFIRMED the same row reads as
                  the STATE -- the founder's distinction in one control. A
                  staged value is still the verb, because it is not a state
                  until a server says so. */}
              {a.status === confirmedValue && staged === null
                ? planItemStatusLabel(a.status)
                : a.verb}
            </option>
          ))}
        </AcSelect>
      </FormField>}

      {/* REQUIRED REASONS PRESERVED: same vocabulary, same "No reason" opening
          entry, shown only for a DECLINED decision -- staged or confirmed. */}
      {shownValue === 'declined' ? (
        <FormField id={reasonId} label={CASE_SHEET_LABELS.declineReason}>
          <AcSelect
            id={reasonId}
            aria-label={`${CASE_SHEET_LABELS.declineReason} for ${procedureName}`}
            value={shownReason}
            disabled={busy}
            data-testid={`${testId ?? 'plan-decision'}-reason`}
            onChange={(e) =>
              setStaged({ status: 'declined', declineReason: e.target.value })
            }
          >
            <option value="">No reason</option>
            {OUTCOME_REASON_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </AcSelect>
        </FormField>
      ) : null}

      {/* THE DELIBERATE COMMIT. The shipped plan-row editor's own shape
          (ConsultWorkspacePage.tsx:2629-2636): one Save beside the select, so
          an intermediate selection writes nothing. It appears only when there
          is something uncommitted, so a settled row carries no dead control. */}
      {dirty && presentation !== 'segments' ? (
        <AcButton
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => {
            if (staged === null) return;
            onCommit(staged.status as PlanDecisionAction, staged.declineReason);
          }}
          data-testid={`${testId ?? 'plan-decision'}-save`}
        >
          {outcome.kind === 'saving' ? 'Saving...' : PLAN_DECISION_SAVE_LABEL}
        </AcButton>
      ) : null}

      {/* WHAT HAPPENED, and the value that is actually recorded. Never the
          requested one: the staging is already cleared by the effect above. */}
      {settledBadly ? (
        <p
          role="alert"
          data-testid={`${testId ?? 'plan-decision'}-outcome`}
          data-failure={outcome.kind === 'failed' ? outcome.failure : undefined}
        >
          <strong>{PLAN_DECISION_UNCONFIRMED_PREFIX}.</strong>{' '}
          {outcome.kind === 'failed' ? PLAN_DECISION_FAILURE_COPY[outcome.failure] : ''}{' '}
          {PLAN_DECISION_LAST_CONFIRMED_PREFIX}: {presentation === 'segments' && status === 'advised' ? planItemStatusLabel(status) : planDecisionDisplay(status)}
          {status === 'declined' && declineReason !== ''
            ? ` (${outcomeReasonLabel(declineReason)})`
            : ''}
          .
        </p>
      ) : null}
    </div>
  );
}
